import { useMemo, useState } from 'react'
import {
  BookOpen,
  Database,
  ListChecks,
  Loader2,
  Luggage,
  Map as MapIcon,
  Printer,
  Route,
  Wallet,
} from 'lucide-react'

import { AuthProvider } from './auth/AuthProvider'
import { useAuth } from './auth/authContext'
import { LoginScreen } from './auth/LoginScreen'
import { SyncBar } from './components/SyncBar'
import { RoadbookProvider } from './data/RoadbookProvider'
import { useRoadbook } from './data/roadbookContext'
import { TripProvider } from './data/TripProvider'
import { useTrip } from './data/tripContext'
import { BudgetView } from './features/budget/BudgetView'
import { ChecklistView } from './features/checklist/ChecklistView'
import { DataView } from './features/data/DataView'
import { DiaryView } from './features/diary/DiaryView'
import { MapView } from './features/map/MapView'
import { PrintView } from './features/print/PrintView'
import { StagesView } from './features/stages/StagesView'
import { TripPicker } from './features/trip/TripPicker'
import { TripView } from './features/trip/TripView'
import { anzahl, hours, num, zeile } from './lib/format'
import { tripTotals } from './lib/trip'
import { fahrzeug } from './lib/vehicle'
import { TABS, useTab, type Tab } from './useTab'

const TAB_LABELS: Record<Tab, { label: string; Icon: typeof Route }> = {
  etappen: { label: 'Etappen', Icon: Route },
  karte: { label: 'Karte', Icon: MapIcon },
  tagebuch: { label: 'Tagebuch', Icon: BookOpen },
  budget: { label: 'Budget', Icon: Wallet },
  checkliste: { label: 'Checkliste', Icon: ListChecks },
  reise: { label: 'Reise', Icon: Luggage },
  daten: { label: 'Daten', Icon: Database },
  druck: { label: 'Drucken', Icon: Printer },
}

/**
 * Die Reiter, die in der Leiste nebeneinander stehen.
 *
 * Der Druckbereich fehlt hier mit Absicht: er bekommt den Platz rechts, den
 * bisher der Knopf "Drucken" hatte. Zwei Wege zu derselben Ansicht in einer
 * Leiste wären nur Verwirrung.
 */
const LEISTE = TABS.filter((key) => key !== 'druck')

export default function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  )
}

/** Entscheidet, ob die App läuft, lädt oder erst eine Anmeldung braucht. */
function Gate() {
  const { status } = useAuth()

  if (status === 'loading') {
    return (
      <div className="mittig">
        <Loader2 size={20} className="dreht" />
        <p>Roadbook wird geladen …</p>
      </div>
    )
  }

  if (status === 'signed-out' || status === 'error') {
    return <LoginScreen />
  }

  return (
    <TripProvider>
      <ReiseGate />
    </TripProvider>
  )
}

/**
 * Entscheidet, ob eine Reise offen ist oder erst eine zu wählen ist.
 *
 * Der key am RoadbookProvider ist der Kern der Trennung zwischen zwei Reisen:
 * beim Wechsel wird der ganze Provider abgebaut und neu aufgebaut. Kein
 * Zustand, kein Zwischenspeicher und kein laufender Abgleich der alten Reise
 * überlebt das - sie hängen an einer Instanz, die es nicht mehr gibt. Ohne
 * den key würde React denselben Baum weiterverwenden und die Dokumente der
 * einen Reise stünden für einen Wimpernschlag in der anderen.
 */
function ReiseGate() {
  const { phase, aktiv } = useTrip()

  if (phase !== 'offen' || !aktiv) return <TripPicker />

  return (
    <RoadbookProvider key={aktiv.id}>
      <Shell />
    </RoadbookProvider>
  )
}

function Shell() {
  const { ready } = useRoadbook()
  const [tab, setTab] = useTab()
  const [selectedId, setSelectedId] = useState('')

  if (!ready) {
    return (
      <div className="mittig">
        <Loader2 size={20} className="dreht" />
        <p>Lokaler Bestand wird gelesen …</p>
      </div>
    )
  }

  return (
    <>
      {/*
        Im Druckbereich ist das Buch das Dokument, und der Kopf der App gehört
        nicht auf dessen erste Seite. Aus jedem anderen Reiter heraus darf
        weiterhin gedruckt werden, was da ist - dann mit Kopf.
      */}
      <Header imDruck={tab === 'druck'} />

      <nav className="reiter kein-druck" aria-label="Bereiche">
        {LEISTE.map((key) => {
          const { label, Icon } = TAB_LABELS[key]
          return (
            <button
              key={key}
              type="button"
              aria-current={tab === key ? 'page' : undefined}
              onClick={() => setTab(key)}
            >
              <Icon size={15} /> {label}
            </button>
          )
        })}
        {/*
          Führt in den Druckbereich, statt window.print() zu rufen. Der Weg
          über den Vorbereitungsschirm ist kein Umweg: ohne den Hinweis auf die
          Hintergrundgrafiken im Druckdialog kommt die Hälfte der Flächen weiss
          heraus, und das fällt erst auf dem Papier auf.
        */}
        <button
          type="button"
          className="rechts"
          aria-current={tab === 'druck' ? 'page' : undefined}
          onClick={() => {
            setTab('druck')
          }}
        >
          <Printer size={15} /> Drucken
        </button>
      </nav>

      <StatusZeile hidden={tab === 'druck'} />

      {tab === 'etappen' && (
        <StagesView
          selectedId={selectedId}
          onSelect={(id) => {
            setSelectedId(id)
          }}
        />
      )}
      {tab === 'karte' && (
        <MapView
          selectedId={selectedId}
          onSelect={(id) => {
            setSelectedId(id)
          }}
        />
      )}
      {tab === 'tagebuch' && <DiaryView />}
      {tab === 'budget' && <BudgetView />}
      {tab === 'checkliste' && <ChecklistView />}
      {tab === 'reise' && <TripView />}
      {tab === 'daten' && <DataView />}
      {tab === 'druck' && <PrintView />}
    </>
  )
}

function StatusZeile({ hidden }: { hidden: boolean }) {
  const { sync, syncNow } = useRoadbook()
  return (
    <div className={hidden ? 'kein-druck' : undefined}>
      <SyncBar status={sync} onSync={syncNow} />
    </div>
  )
}

function Header({ imDruck }: { imDruck: boolean }) {
  const { trip, diary, darfSchreiben } = useRoadbook()
  const { aktiv } = useTrip()
  const totals = useMemo(() => tripTotals(trip, diary), [trip, diary])

  // Solange im Reiter Reise nichts eingetragen ist, bleiben die Felder leer -
  // und dann darf im Kopf kein Trennpunkt vor dem Nichts stehen und kein
  // "Start" ohne Datum dahinter. Der Titel fällt auf den Namen zurück, unter
  // dem die Reise angelegt wurde: eine Überschrift, die gar nicht dasteht, ist
  // schlimmer als eine vorläufige.
  //
  // Auf dem Schild stand fest "CH" und daneben das erste Wort des
  // Fahrzeugnamens. Das Zeichen gehört ans Fahrzeug - eine Reise mit einem
  // deutschen Wagen trug hier trotzdem CH -, und ohne beides gibt es auch kein
  // Schild: ein leeres Oval ist keine Auskunft.
  const auto = fahrzeug(trip)
  const schild = zeile([auto.sign, auto.name.split(' ')[0]])
  const unterzeile = zeile([
    auto.name,
    anzahl(totals.stageCount, 'Etappe', 'Etappen'),
    trip.meta.start && `Start ${trip.meta.start}`,
  ])

  return (
    <header className={imDruck ? 'kopf kein-druck' : 'kopf'}>
      <div className="marke">
        {schild && <span className="schild">{schild}</span>}
        <div>
          <h1>{trip.meta.title || aktiv?.name || 'Ohne Titel'}</h1>
          <p className="kopf-unterzeile">
            {unterzeile}
            {/*
              Wer nur zusehen darf, soll es nicht erst daran merken, dass die
              Knöpfe fehlen. Auf dem Papier hat der Hinweis nichts zu suchen:
              dort steht die Reise, nicht die Berechtigung.
            */}
            {!darfSchreiben && <span className="marker kein-druck">Nur lesen</span>}
          </p>
        </div>
      </div>

      <div className="tacho">
        <div>
          <b>{num(totals.km)}</b>
          <span>km geplant</span>
        </div>
        <div>
          <b>{num(totals.drivenKm)}</b>
          <span>km gefahren</span>
        </div>
        <div>
          <b>{hours(totals.hours)}</b>
          <span>h am Steuer</span>
        </div>
      </div>
    </header>
  )
}
